import Urn from '@adc/structures/urn'
import React from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import {
  DataTableWrapper,
  PageLayout,
  withApplicationNotificationsContext,
} from '@adc/ui-morpheus'
import { Link } from 'react-router-dom'
import { ROOT_CRUMB } from '../../constants/routing'
import { TRANSACTIONS } from '../../constants/tableTypes'
import {
  fetchTable,
  fetchTableAppend,
  fetchTableBatches,
  addFilter,
  clearFilters,
  removeFilter,
  changeSortBy,
  abortTableValues,
  resetTableValues,
} from '../../actions/transactions'
import {
  columnSelector,
  decomposeUrn,
  getFilterClauses,
} from '../../reducers/transactions/columnDefinitions'
import { areRolesResiOnly } from '../../util/roles'

/**
 * Exported from statics, since the whole notification was static. If we want to have project-wide constants
 * for these, something like `constants/notifications.js`, where export exported member has the full notification
 * interface would be preferable.
 *
 * For now, keeping this as a scoped constant for exactly where it is used.
 */
const NOT_RESIDENTIAL_USER_ROLES_NOTIFICATION = {
  title: 'Sorry...',
  message: 'You may not edit transactions that have non-RESI roles.',
}

const TransactionsPage = props => {
  const {
    columns,
    filterClauses,
    addFilter,
    removeFilter,
    clearFilters,
    changeSortBy,
    fetchDataRows,
    fetchDataRowsAppend,
    isLoading,
    errorInfo,
    rowData,
    totalCount,
    suggestions,
    roleDefs,
    notifications,
    config,
    downloadData,
    downloadActions,
  } = props

  const checkEditPermission = rowIndex => {
    const roles = _.get(rowData[rowIndex], 'security.roles', [])

    if (!areRolesResiOnly(roles, roleDefs)) {
      notifications.toast.open({
        role: 'warn',
        message: NOT_RESIDENTIAL_USER_ROLES_NOTIFICATION,
      })
    }
  }

  const onRowAction = rowIndex => {
    checkEditPermission(rowIndex)
  }

  const tableProps = {
    columns,
    filterClauses,
    addFilter,
    removeFilter,
    clearFilters,
    changeSortBy,
    fetchDataRows,
    fetchDataRowsAppend,
    isLoading,
    errorInfo,
    rowData,
    totalCount,
    onRowAction,
    suggestions,
    resetForm: PropTypes.func.isRequired,
    downloadData,
    downloadActions,
    customFormatters: {
      urnFormatter: (value, definition, rowData) => {
        const { resourceId, resourceType } = decomposeUrn(value) || {}
        switch (resourceType) {
          case 'property': {
            return (
              <Link
                to={`/property/properties/${resourceId}/view`}
                target='_blank'
                rel='noopener noreferrer'
              >
                {resourceId}
              </Link>
            )
          }
          case 'listing': {
            return (
              <Link
                to={`/property/listings/${resourceId}/view`}
                target='_blank'
                rel='noopener noreferrer'
              >
                {resourceId}
              </Link>
            )
          }
        }
        return resourceId || 'N/A'
      },
      leadIntakeFormatter: value => {
        if (!Array.isArray(value)) return 'N/A'
        const lead = value.find(u => u.urn_type === 'INTAKE_PROPERTY_LEAD')
        if (!lead) return '—'
        return (
          Urn.safelyParse(lead.associated_urn)?.identifier ??
          lead.associated_urn
        )
      },
      organizationFormatter: value => {
        if (!Array.isArray(value)) return 'N/A'
        const org = value.find(u => u.urn_type === 'ORGANIZATION')
        if (!org) return '—'
        return (
          Urn.safelyParse(org.associated_urn)?.identifier ?? org.associated_urn
        )
      },
    },
  }

  return (
    <PageLayout title='Transactions' crumbs={[ROOT_CRUMB, 'Transactions']}>
      <DataTableWrapper {...config} {...tableProps} />
    </PageLayout>
  )
}

TransactionsPage.propTypes = {
  config: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  filterClauses: PropTypes.array.isRequired,
  addFilter: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  changeSortBy: PropTypes.func.isRequired,
  fetchDataRows: PropTypes.func.isRequired,
  fetchDataRowsAppend: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  errorInfo: PropTypes.object,
  rowData: PropTypes.array.isRequired,
  totalCount: PropTypes.number,
  suggestions: PropTypes.object,
  roleDefs: PropTypes.array.isRequired,
  notifications: PropTypes.object.isRequired,
  downloadData: PropTypes.object.isRequired,
  downloadActions: PropTypes.shape({
    fetchData: PropTypes.func.isRequired,
    abort: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
  }),
}

const getTotalCount = state => state.total_count

const mapStateToProps = store => {
  const { content, isLoading, errorInfo } = store[TRANSACTIONS].data
  const downloadDataRaw = store[TRANSACTIONS].downloadData
  const downloadContent = downloadDataRaw.content
  const downloadData = {
    content: downloadContent || [],
    totalCount: downloadDataRaw.total_count,
    totalRetrieved: downloadDataRaw.total_elements,
    errorInfo: downloadDataRaw.errorInfo,
    isBatchSuccessful: downloadDataRaw.isBatchSuccessful,
  }
  const config = store[TRANSACTIONS].tableInfo.config
  return {
    config,
    isLoading: !!isLoading,
    columns: columnSelector(store[TRANSACTIONS].tableInfo),
    filterClauses: getFilterClauses(store[TRANSACTIONS].tableInfo),
    totalCount: getTotalCount(store[TRANSACTIONS].data),
    rowData: content,
    errorInfo,
    roleDefs: store.roles.content,
    downloadData,
  }
}

const mapDispatchToProps = dispatch => ({
  fetchDataRows: fetchOptions => dispatch(fetchTable(fetchOptions)),
  fetchDataRowsAppend: fetchOptions => dispatch(fetchTableAppend(fetchOptions)),
  addFilter: filterClause => dispatch(addFilter(filterClause)),
  removeFilter: filterClause => dispatch(removeFilter(filterClause)),
  clearFilters: _ => dispatch(clearFilters()),
  changeSortBy: (sortValues, columns) =>
    dispatch(changeSortBy(sortValues, columns)),
  downloadActions: {
    fetchData: () => dispatch(fetchTableBatches()),
    abort: () => dispatch(abortTableValues(TRANSACTIONS, true)),
    reset: () => dispatch(resetTableValues(TRANSACTIONS, true)),
  },
})

export default withApplicationNotificationsContext(
  connect(mapStateToProps, mapDispatchToProps)(TransactionsPage)
)
